/**
 * The Case filter groups, in one list. The URL schema, the request params, the
 * filter count, the menu, the chips and the invalid-filter notice all read it,
 * so a new group is one entry here — not one edit per surface.
 *
 * The list order is the menu order, and every surface that names the groups
 * follows it.
 */
import { CASE_STATUS_FILTER_OPTIONS } from '@/features/air/case-status-preset'
import {
	POLICY_SEVERITY_FILTER_OPTIONS,
	POLICY_TYPE_FILTER_OPTIONS,
} from '@/features/air/policy-vocabulary'

import { toDisplayName } from '@/shared/parsing/utils/text'

import { INVESTIGATION_FILTER_OPTIONS } from './investigation-display'

import type { FilterValueOption } from '@/shared/data-table/types/filters'

/**
 * Each group's `options` are read from the records that own each axis's
 * spelling — never a second hand-written list (ADR 0020 §5). Their values are
 * the case-list endpoint's own, so they are also the group's allow-list.
 *
 * - `options: null` — Source. Its values are connector definition ids, offered
 *   from the integrations lists (`SourceFilterItems`); the API has no
 *   allow-list, and an unknown id returns no Cases, not 400.
 * - `needsInvestigation` — offered only where the Investigation rule is on. A
 *   hidden group must not narrow the list with no visible reason, so it gets no
 *   param, count or chip either.
 *
 * Severity uses the policy record on purpose: a Case's severity is the severity
 * of the policy that fired it, the same three-member union.
 */
export const CASE_FILTER_GROUPS = [
	{
		key: 'status',
		label: 'Status',
		options: CASE_STATUS_FILTER_OPTIONS,
		needsInvestigation: false,
	},
	{
		key: 'severity',
		label: 'Severity',
		options: POLICY_SEVERITY_FILTER_OPTIONS,
		needsInvestigation: false,
	},
	{ key: 'source', label: 'Source', options: null, needsInvestigation: false },
	{
		key: 'type',
		label: 'Type',
		options: POLICY_TYPE_FILTER_OPTIONS,
		needsInvestigation: false,
	},
	{
		key: 'investigation',
		label: 'Investigation',
		options: INVESTIGATION_FILTER_OPTIONS,
		needsInvestigation: true,
	},
] as const satisfies ReadonlyArray<{
	key: string
	label: string
	options: ReadonlyArray<FilterValueOption> | null
	needsInvestigation: boolean
}>

export type CaseFilterGroup = (typeof CASE_FILTER_GROUPS)[number]

export type CaseFilterKey = CaseFilterGroup['key']

/** A group's value type: its options' values, or any string for Source. */
type GroupValue<G extends CaseFilterGroup> =
	G['options'] extends ReadonlyArray<{ value: infer V }> ? V : string

/** The `filters` URL param: each group, when set, as an array of API values. */
export type CaseFilters = {
	[G in CaseFilterGroup as G['key']]?: Array<GroupValue<G>>
}

/** The groups on screen: all of them, or all but those that need Investigation. */
export const visibleCaseFilterGroups = (showInvestigation: boolean) =>
	CASE_FILTER_GROUPS.filter(
		(group) => showInvestigation || !group.needsInvestigation,
	)

/**
 * Each group's label by key. `Object.fromEntries` cannot type its keys; the
 * list holds every key, so the record is complete.
 */
export const CASE_FILTER_LABELS = Object.fromEntries(
	CASE_FILTER_GROUPS.map(({ key, label }) => [key, label]),
) as Record<CaseFilterKey, string>

/**
 * `undefined` for a missing or empty group, so an empty group never reaches
 * the URL, the query key or the request.
 */
export const nonEmpty = <T>(list: Array<T> | undefined) =>
	list?.length ? list : undefined

/**
 * The display label of a value in `options`. The schema only lets known
 * values into the URL, so the fallback is for a value added to the API before
 * the options — it still reads, instead of rendering blank (P-17).
 */
export const caseFilterValueLabel = (
	options: ReadonlyArray<FilterValueOption>,
	value: string,
): string =>
	options.find((option) => option.value === value)?.label ??
	toDisplayName(value)
